import { User, UserMin } from '@apiContract';
import { skipToken } from '@reduxjs/toolkit/query';
import { useGetGroupsForPickQuery } from 'src/redux/api/groups';
import { useGetLockersByUserIdQuery } from 'src/redux/api/lockers';
import { getRoutesList } from 'src/utils/mappers';

import { useRoutesByLocation } from './useRoutesByLocation';
import { useSettings } from './useSettings';
import { useUserLocation } from './useUserLocation';

type Settings = {
  AssignmentCardShowMiddleName: boolean;
  AssignmentCardShowUserId: boolean;
  AssignmentCardShowAutoAssignId: boolean;
  AssignmentCardShowRoutes: boolean;
  AssignmentCardShowLocation: boolean;
  AssignmentCardShowGroup: boolean;
  AssignmentCardShowLockerBanks: boolean;
  LockersWorkflowEnable: boolean;
};

const getStringValue = (str?: any) => (str ? str : '');

const getString = (value1: any, value2: any) => {
  return `${getStringValue(value1)}${getStringValue(value1 && value2 && ' • ')}${getStringValue(value2)}`;
};

export const useUserInfoLines = (user?: User | UserMin | null, showLocker = false) => {
  const settings = useSettings([
    'AssignmentCardShowMiddleName',
    'AssignmentCardShowUserId',
    'AssignmentCardShowAutoAssignId',
    'AssignmentCardShowRoutes',
    'AssignmentCardShowLocation',
    'AssignmentCardShowGroup',
    'AssignmentCardShowLockerBanks',
    'LockersWorkflowEnable',
  ]) as Settings;

  const { data, isLoading: isLoadingGroups } = useGetGroupsForPickQuery(undefined, { skip: !user?.groupIds?.length });

  const isShowLockerLine =
    showLocker && !!user?.id && settings.AssignmentCardShowLockerBanks && settings.LockersWorkflowEnable;

  const { location, isLoading: isLocationResponseLoading } = useUserLocation(user);
  const { isLoading: isRoutesLoading, data: routes } = useRoutesByLocation(location?.id);
  const { data: lockerBanksData, isLoading: isLoadingLocker } = useGetLockersByUserIdQuery(user?.id || skipToken, {
    skip: !isShowLockerLine,
  });

  const userID = settings.AssignmentCardShowUserId && user?.userName;
  const userAutoassignId = settings.AssignmentCardShowAutoAssignId && user?.autoAssignId;
  const userLocation = settings.AssignmentCardShowLocation && location?.name;
  const userRoute = settings.AssignmentCardShowRoutes && getRoutesList(routes);
  const userGroups = user?.groupIds?.map((id) => data?.[id]?.name);
  const lockerLine = isShowLockerLine
    ? lockerBanksData?.reduce((acc, { name }) => `${acc}${acc ? ',' : ''} ${name}`, '')
    : null;

  return {
    idLine: getString(userID, userAutoassignId),
    routesLine: getString(userRoute, userLocation),
    isRoutesLoading: isLocationResponseLoading || isRoutesLoading,
    isLoadingGroups,
    userGroups,
    showGroups: settings.AssignmentCardShowGroup,
    isShowLockerLine,
    lockerLine,
    isLoadingLocker,
  };
};
